import {
  ACTION,
  ARRAY,
  BOOLEAN,
  MULTIPLE,
  NUMBER,
  OBJECT,
  STRING,
} from '../../component-validations';
import { ACTIVE_CLASS, FORBIDS_MODEL } from '../constants/links';
import {
  ALIGN_CONTENT_OPTIONS,
  ALIGN_ITEMS_OPTIONS,
  ALIGN_SELF_OPTIONS,
  FLEX_WRAP_OPTIONS,
  FORBIDS_FLEXBOX,
  FORBIDS_GRID,
  GRID_AUTO_FLOW_OPTIONS,
  INITIAL,
  JUSTIFY_CONTENT_OPTIONS,
  JUSTIFY_ITEMS_OPTIONS,
  JUSTIFY_SELF_OPTIONS,
  REQUIRES_GRID,
} from '../constants/css-layout';
import {
  ARIA_AUTOCOMPLETE_OPTIONS,
  ARIA_CURRENT_OPTIONS,
  ARIA_HASPOPUP_OPTIONS,
  ARIA_LIVE_OPTIONS,
  ARIA_ORIENTATION_OPTIONS,
  ARIA_ROLE_OPTIONS,
} from '../constants/aria';
import {
  BOTTOM_LEFT,
  PLACEMENT_OPTIONS,
  TOP_MIDDLE,
  VERTICAL_PLACEMENT_OPTIONS,
} from '../constants/positioning';
import { BUTTON_TYPES, PRIMARY } from '../constants/buttons';
import { SUPPORTED_LAYOUTS, SUPPORTED_TYPES } from '../constants/inputs';

/**
 * Central registry of all shared prop configurations.
 * Import this single object instead of multiple named exports.
 */
const SHARED_ARGS = {
  /**
   * Common accessibility args
   */
  accessibility: {
    meta: {
      ariaActiveDescendant: {
        description: 'ID of the currently active descendant element',
        htmlAttribute: 'aria-activedescendant',
        type: STRING,
      },
      ariaAtomic: {
        defaultValue: false,
        description:
          'Indicates whether assistive technologies present the entire region or only changed parts',
        htmlAttribute: 'aria-atomic',
        type: BOOLEAN,
      },
      ariaAutoComplete: {
        description: 'Indicates whether user input completion suggestions are provided',
        htmlAttribute: 'aria-autocomplete',
        options: ARIA_AUTOCOMPLETE_OPTIONS,
        type: STRING,
      },
      ariaBusy: {
        defaultValue: false,
        description:
          'Indicates an element is being modified and assistive technologies should wait',
        htmlAttribute: 'aria-busy',
        type: BOOLEAN,
      },
      ariaControls: {
        description: 'ID of element(s) controlled by this component',
        htmlAttribute: 'aria-controls',
        type: STRING,
      },
      ariaCurrent: {
        description: 'Indicates the current item within a set',
        htmlAttribute: 'aria-current',
        options: ARIA_CURRENT_OPTIONS,
        type: STRING,
      },
      ariaDescribedBy: {
        description: 'ID of element that describes this component',
        htmlAttribute: 'aria-describedby',
        type: STRING,
      },
      ariaDisabled: {
        defaultValue: false,
        description: 'Indicates the element is perceivable but disabled',
        htmlAttribute: 'aria-disabled',
        type: BOOLEAN,
      },
      ariaExpanded: {
        description: 'Indicates whether a collapsible element is expanded or collapsed',
        htmlAttribute: 'aria-expanded',
        type: BOOLEAN,
      },
      ariaHasPopup: {
        description: 'Indicates the type of popup triggered by this element',
        htmlAttribute: 'aria-haspopup',
        options: ARIA_HASPOPUP_OPTIONS,
        type: STRING,
      },
      ariaHidden: {
        description: 'Indicates whether the element is hidden from assistive technologies',
        htmlAttribute: 'aria-hidden',
        type: BOOLEAN,
      },
      ariaInvalid: {
        defaultValue: false,
        description: 'Indicates the entered value does not conform to expected format',
        htmlAttribute: 'aria-invalid',
        type: BOOLEAN,
      },
      ariaLabel: {
        description: 'Accessible label for screen readers',
        htmlAttribute: 'aria-label',
        type: STRING,
      },
      ariaLabelledBy: {
        description: 'ID of element that labels this component',
        htmlAttribute: 'aria-labelledby',
        type: STRING,
      },
      ariaLive: {
        description: 'Indicates how assistive technologies announce updates to the element',
        htmlAttribute: 'aria-live',
        options: ARIA_LIVE_OPTIONS,
        type: STRING,
      },
      ariaModal: {
        defaultValue: false,
        description: 'Indicates whether an element is modal when displayed',
        htmlAttribute: 'aria-modal',
        type: BOOLEAN,
      },
      ariaOrientation: {
        description: 'Indicates the orientation of the element',
        htmlAttribute: 'aria-orientation',
        options: ARIA_ORIENTATION_OPTIONS,
        type: STRING,
      },
      ariaOwns: {
        description: 'ID of element(s) owned by this component',
        htmlAttribute: 'aria-owns',
        type: STRING,
      },
      ariaPressed: {
        description: 'Indicates the current pressed state of a toggle button',
        htmlAttribute: 'aria-pressed',
        type: BOOLEAN,
      },
      ariaReadOnly: {
        defaultValue: false,
        description: 'Indicates the element is not editable but is otherwise operable',
        htmlAttribute: 'aria-readonly',
        type: BOOLEAN,
      },
      ariaRequired: {
        defaultValue: false,
        description: 'Indicates user input is required before form submission',
        htmlAttribute: 'aria-required',
        type: BOOLEAN,
      },
      ariaSelected: {
        description: 'Indicates the current selected state',
        htmlAttribute: 'aria-selected',
        type: BOOLEAN,
      },
      role: {
        description: 'ARIA role attribute for the component',
        options: ARIA_ROLE_OPTIONS,
        type: STRING,
      },
    },
  },

  /**
   * Common action handler args
   */
  actions: {
    meta: {
      beforeClose: {
        description: 'Action triggered before the component is closed',
        type: ACTION,
      },
      onApply: {
        description:
          'Action triggered when the footer Apply button is clicked; receives the array of checked values',
        type: ACTION,
      },
      onCancel: {
        description: 'Action triggered when the footer cancel/dismiss action is invoked',
        type: ACTION,
      },
      onClearAll: {
        description: 'Action triggered after the footer Clear all button clears the selection',
        type: ACTION,
      },
      close: {
        description: 'Action triggered to close/dismiss the component',
        type: ACTION,
      },
      onBeforeClose: {
        description: 'Action triggered before the component is closed',
        type: ACTION,
      },
      onBlur: {
        description: 'Action triggered when the input loses focus',
        type: ACTION,
      },
      onChange: {
        description: 'Action triggered when the input value changes (on blur)',
        type: ACTION,
      },
      onClick: {
        description: 'Action triggered when the component is clicked',
        type: ACTION,
      },
      onFocus: {
        description: 'Action triggered when the input gains focus',
        type: ACTION,
      },
      onInput: {
        description: 'Action triggered on each input keystroke',
        type: ACTION,
      },
      onKeyDown: {
        description: 'Action triggered when a key is pressed down',
        type: ACTION,
      },
      onKeyUp: {
        description: 'Action triggered when a key is released',
        type: ACTION,
      },
      onClose: {
        description: 'Action triggered when the component is closed',
        type: ACTION,
      },
      onOpen: {
        description: 'Action triggered when the component is opened',
        type: ACTION,
      },
      onSelect: {
        description: 'Action triggered when a selection is made',
        type: ACTION,
      },
      onSelectAll: {
        description:
          'Action triggered when the select-all option is toggled (additive to @selectAll)',
        type: ACTION,
      },
    },
  },

  /**
   * Args shared by every surface that positions itself against an anchor element in the browser's
   * top layer: `Spds::Core::Menu::Base` and everything built on it, and `Spds::Feedback::Tooltip`.
   *
   * A surface narrows `placement` to the subset it supports by overriding `options` when it picks
   * the arg — the menu restricts itself to `VERTICAL_PLACEMENT_OPTIONS`, while the tooltip accepts
   * all eight. `anchorOffset` is a CSS length rather than a bare number so a consumer can tie the
   * gap to type scale or a container; `utils/spds/css/length` resolves it for the positioning math.
   * The tooltip renames it to `offset` for its public API.
   */
  anchored: {
    meta: {
      anchorElement: {
        description: 'Element the surface positions itself against',
        required: true,
        type: OBJECT,
      },
      anchorOffset: {
        description: 'Gap between the anchor and the surface, as a CSS length',
        type: STRING,
      },
      placement: {
        defaultValue: BOTTOM_LEFT,
        description:
          'Preferred position of the surface relative to its anchor. Only a starting point: the surface flips to the other side of the anchor when it would overflow the viewport, and shifts its alignment when it would overflow horizontally.',
        options: PLACEMENT_OPTIONS,
        type: STRING,
      },
    },
  },

  /**
   * Common button args for dropdown/split buttons
   */
  button: {
    meta: {
      buttonType: {
        defaultValue: PRIMARY,
        description: 'Visual style variant of the button',
        options: BUTTON_TYPES,
        type: STRING,
      },
      label: {
        description: 'Text label displayed on the button',
        type: STRING,
      },
      pill: {
        defaultValue: false,
        description: 'Round the button corners into a pill shape',
        type: BOOLEAN,
      },
    },
  },

  /**
   * Common component state args
   */
  componentState: {
    meta: {
      animated: {
        defaultValue: false,
        description: 'Enables animations for state changes',
        type: BOOLEAN,
      },
      autofocus: {
        defaultValue: false,
        description: 'Automatically focus the component on render',
        type: BOOLEAN,
      },
      disabled: {
        defaultValue: false,
        description: 'Disables user interaction with the component',
        type: BOOLEAN,
      },
      error: {
        defaultValue: false,
        description: 'Indicates the component is in an error state',
        type: BOOLEAN,
      },
      hasBorder: {
        defaultValue: false,
        description: 'Display a border on the component',
        type: BOOLEAN,
      },
      hidden: {
        defaultValue: false,
        description: 'Hides the component from view',
        type: BOOLEAN,
      },
      id: {
        description: 'Unique identifier for the component instance',
        type: STRING,
        initialValue: '',
      },
      open: {
        defaultValue: false,
        description: 'Controls the open/closed state of the component',
        type: BOOLEAN,
      },
    },
  },

  /**
   * Common content args
   */
  content: {
    meta: {
      content: {
        description: 'Content to render for the component',
        type: STRING,
      },
      icon: {
        description: 'Icon to display',
        type: STRING,
      },
      iconOnly: {
        defaultValue: false,
        description: 'Component only displays an icon without text',
        type: BOOLEAN,
      },
      subtitle: {
        description: 'Subtitle text for the component',
        type: STRING,
      },
      text: {
        description: 'Text content for the component',
        type: STRING,
      },
      title: {
        description: 'Title text for the component',
        type: STRING,
      },
    },
  },

  /**
   * Common container args
   */
  container: {
    meta: {
      column: {
        defaultValue: false,
        description: 'Arrange children in a vertical column',
        forbids: FORBIDS_GRID,
        type: BOOLEAN,
      },
      fitContent: {
        defaultValue: false,
        description: 'Fit container to content size (width and height)',
        type: BOOLEAN,
      },
      fitContentHeight: {
        defaultValue: false,
        description: 'Fit container height to content',
        type: BOOLEAN,
      },
      fitContentWidth: {
        defaultValue: false,
        description: 'Fit container width to content',
        type: BOOLEAN,
      },
      grid: {
        defaultValue: false,
        description: 'Use CSS Grid layout for the container',
        forbids: FORBIDS_FLEXBOX,
        type: BOOLEAN,
      },
      inline: {
        defaultValue: false,
        description: 'Display container inline',
        type: BOOLEAN,
      },
      reverse: {
        defaultValue: false,
        description: 'Reverse the order of child elements',
        forbids: FORBIDS_GRID,
        type: BOOLEAN,
      },
      tag: {
        defaultValue: 'div',
        description: 'HTML tag to use for the container element',
        type: STRING,
      },
    },
  },

  /**
   * Props shared by both flex and grid layouts
   */
  flexAndGrid: {
    meta: {
      alignContent: {
        cssProperty: 'align-content',
        defaultValue: INITIAL,
        description: 'Aligns rows within the container when there is extra space',
        options: ALIGN_CONTENT_OPTIONS,
        type: STRING,
      },
      alignItems: {
        cssProperty: 'align-items',
        defaultValue: INITIAL,
        description: 'Aligns items along the cross axis',
        options: ALIGN_ITEMS_OPTIONS,
        type: STRING,
      },
      alignSelf: {
        cssProperty: 'align-self',
        defaultValue: INITIAL,
        description: 'Overrides align-items for individual items',
        options: ALIGN_SELF_OPTIONS,
        type: STRING,
      },
      gap: {
        cssProperty: 'gap',
        defaultValue: INITIAL,
        description: 'Space between items (supports CSS units)',
        type: STRING,
      },
      justifyContent: {
        cssProperty: 'justify-content',
        defaultValue: INITIAL,
        description: 'Aligns items along the main axis',
        options: JUSTIFY_CONTENT_OPTIONS,
        type: STRING,
      },
    },
  },

  /**
   * Props specific to flex layouts only
   */
  flexOnly: {
    meta: {
      flex: {
        cssProperty: 'flex',
        defaultValue: INITIAL,
        description: 'Flex grow/shrink/basis shorthand',
        forbids: FORBIDS_GRID,
        initialValue: '',
        type: STRING,
      },
      flexBasis: {
        cssProperty: 'flex-basis',
        defaultValue: INITIAL,
        description: 'Flex basis',
        forbids: FORBIDS_GRID,
        initialValue: '',
        type: STRING,
      },
      flexGrow: {
        cssProperty: 'flex-grow',
        defaultValue: INITIAL,
        description: 'Flex grow factor',
        forbids: FORBIDS_GRID,
        initialValue: '',
        type: STRING,
      },
      flexShrink: {
        cssProperty: 'flex-shrink',
        defaultValue: INITIAL,
        description: 'Flex shrink factor',
        forbids: FORBIDS_GRID,
        initialValue: '',
        type: STRING,
      },
      flexWrap: {
        cssProperty: 'flex-wrap',
        defaultValue: INITIAL,
        description: 'Whether items wrap to new lines',
        forbids: FORBIDS_GRID,
        options: FLEX_WRAP_OPTIONS,
        type: STRING,
      },
      order: {
        cssProperty: 'order',
        defaultValue: INITIAL,
        description: 'Order of the flex item',
        forbids: FORBIDS_GRID,
        initialValue: '',
        type: STRING,
      },
    },
  },

  /**
   * Grid convenience args (simplified API)
   */
  gridConvenience: {
    meta: {
      area: {
        cssProperty: 'grid-area',
        defaultValue: INITIAL,
        description: 'Grid area name (convenience for grid-area)',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      areas: {
        cssProperty: 'grid-template-areas',
        defaultValue: INITIAL,
        description: 'Grid template areas (convenience for grid-template-areas)',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      autoFlow: {
        cssProperty: 'grid-auto-flow',
        defaultValue: INITIAL,
        description: 'Grid auto flow direction (convenience for grid-auto-flow)',
        options: GRID_AUTO_FLOW_OPTIONS,
        requires: REQUIRES_GRID,
        type: STRING,
      },
      columns: {
        cssProperty: 'grid-template-columns',
        defaultValue: INITIAL,
        description: 'Grid columns definition (convenience for grid-template-columns)',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      rows: {
        cssProperty: 'grid-template-rows',
        defaultValue: INITIAL,
        description: 'Grid rows definition (convenience for grid-template-rows)',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
    },
  },

  /**
   * Grid-specific args (full CSS API)
   */
  gridOnly: {
    meta: {
      gridArea: {
        cssProperty: 'grid-area',
        defaultValue: INITIAL,
        description: 'Grid area shorthand',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridAutoColumns: {
        cssProperty: 'grid-auto-columns',
        defaultValue: INITIAL,
        description: 'Size of auto-generated grid columns',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridAutoFlow: {
        cssProperty: 'grid-auto-flow',
        defaultValue: INITIAL,
        description: 'How auto-placed items flow',
        options: GRID_AUTO_FLOW_OPTIONS,
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridAutoRows: {
        cssProperty: 'grid-auto-rows',
        defaultValue: INITIAL,
        description: 'Size of auto-generated grid rows',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridColumn: {
        cssProperty: 'grid-column',
        defaultValue: INITIAL,
        description: 'Grid column position shorthand',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridRow: {
        cssProperty: 'grid-row',
        defaultValue: INITIAL,
        description: 'Grid row position shorthand',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridTemplateAreas: {
        cssProperty: 'grid-template-areas',
        defaultValue: INITIAL,
        description: 'Named grid areas',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridTemplateColumns: {
        cssProperty: 'grid-template-columns',
        defaultValue: INITIAL,
        description: 'Grid column track sizes',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      gridTemplateRows: {
        cssProperty: 'grid-template-rows',
        defaultValue: INITIAL,
        description: 'Grid row track sizes',
        initialValue: '',
        requires: REQUIRES_GRID,
        type: STRING,
      },
      justifyItems: {
        cssProperty: 'justify-items',
        defaultValue: INITIAL,
        description: 'Aligns grid items along the row axis',
        options: JUSTIFY_ITEMS_OPTIONS,
        requires: REQUIRES_GRID,
        type: STRING,
      },
      justifySelf: {
        cssProperty: 'justify-self',
        defaultValue: INITIAL,
        description: 'Aligns a grid item along the row axis',
        options: JUSTIFY_SELF_OPTIONS,
        requires: REQUIRES_GRID,
        type: STRING,
      },
      subgrid: {
        defaultValue: false,
        description: 'Use CSS subgrid to inherit parent grid tracks',
        requires: REQUIRES_GRID,
        type: BOOLEAN,
      },
    },
  },

  /**
   * Routing args for components that can render as an Ember `LinkTo`.
   *
   * `model` and `activeWhen` are typed MULTIPLE because each accepts several data types: a route
   * model may be a string, a number, or an object, and `activeWhen` accepts an array, a string, or
   * a boolean. No single assertion spans those, and typing them narrowly would reject valid
   * values, so MULTIPLE documents the breadth without validating against it.
   */
  link: {
    meta: {
      activeClass: {
        defaultValue: ACTIVE_CLASS,
        description: 'Class applied while the linked route is active',
        type: STRING,
      },
      activeWhen: {
        description:
          'Route name(s) that mark this link as active. Accepts an array of route names, a ' +
          'space-separated string of route names, or a boolean to force the state on or off',
        type: MULTIPLE,
      },
      model: {
        description:
          'Single dynamic segment value for the target route. Cannot be combined with @models',
        type: MULTIPLE,
      },
      models: {
        description: 'Dynamic segment values for the target route, in segment order',
        forbids: FORBIDS_MODEL,
        type: ARRAY,
      },
      query: {
        description: 'Query parameters for the target route',
        type: OBJECT,
      },
      replace: {
        defaultValue: false,
        description: 'Replace the current history entry instead of adding a new one',
        type: BOOLEAN,
      },
      route: {
        description: 'Target route name. Providing this renders the component as a link',
        // Deliberately empty: a non-empty initial value would turn every styleguide button demo
        // into an anchor.
        initialValue: '',
        type: STRING,
      },
    },
  },

  /**
   * Common menu args
   */
  menu: {
    meta: {
      menuAnchorOffset: {
        description:
          'Gap between the anchor and the menu, as a CSS length, passed through from parent components',
        type: STRING,
      },
      menuFitContent: {
        defaultValue: false,
        description: 'Size menu to fit content',
        type: BOOLEAN,
      },
      menuItems: {
        description:
          'Array of menu item objects with label, value, and optional onClick properties',
        type: ARRAY,
      },
      // The menu sizing pass-throughs deliberately carry no defaults: an unset arg falls through to
      // the menu's own default rather than overriding it with an empty value. They are the
      // supported way to size a menu from outside, since the menu writes its sizing as inline
      // custom properties that an app stylesheet cannot outrank.
      menuMaxHeight: {
        description: 'Upper bound on the menu height, as a CSS length',
        type: STRING,
      },
      menuMaxWidth: {
        description: 'Upper bound on the menu width, as a CSS length',
        type: STRING,
      },
      menuMinWidth: {
        description: 'Lower bound on the menu width, as a CSS length',
        type: STRING,
      },
      menuPlacement: {
        description: 'Menu placement passed through from parent components',
        // No `defaultValue`: leaving this undefined lets the menu's own `placement` default
        // govern. The styleguide renders options-backed args as a `<select>` with no empty entry,
        // so it needs a seed value or the control would display `bottom-left` while the bound
        // value is undefined.
        initialValue: BOTTOM_LEFT,
        options: VERTICAL_PLACEMENT_OPTIONS,
        type: STRING,
      },
      menuWidth: {
        description: 'Explicit menu width, as a CSS length',
        type: STRING,
      },
      onItemClick: {
        description: 'Action triggered when a menu item is clicked',
        type: ACTION,
      },
      toggleOpenState: {
        description: 'Function to toggle the open state of the menu',
        type: ACTION,
      },
    },
  },

  /**
   * Common positioning args
   */
  positioning: {
    meta: {
      absolute: {
        cssProperty: 'position',
        defaultValue: false,
        description: 'Position absolute',
        type: BOOLEAN,
      },
      fixed: {
        cssProperty: 'position',
        defaultValue: false,
        description: 'Position fixed',
        type: BOOLEAN,
      },
    },
  },

  /**
   * Common sizing args (explicit width/height and their bounds).
   *
   * Values are CSS length strings, so any valid length, percentage, keyword (`max-content`), or
   * function (`min()`, `calc()`) is accepted.
   *
   * ## Current state
   *
   * Only `Spds::Core::Menu::Base` picks from this collection. It writes each value to an
   * `--spds-menu-`-prefixed custom property that `core/menu/base.module.scss` consumes, because
   * the menu positions itself in the browser top layer and cannot rely on a `width: 100%` that
   * resolves against the viewport.
   *
   * ## Intended end state
   *
   * These should become universal, exposed on `Spds::Core::Container` so every layout primitive
   * accepts them and components stop reaching into DS internals with descendant CSS. That requires:
   *
   * 1. `Spds::Core::Container#propsToMap` — currently `this.grid ? GRID_PROP_NAMES : FLEX_PROP_NAMES`
   *    — to include these names so they reach the element as custom properties.
   * 2. `container.module.scss` to consume the resulting properties.
   * 3. `Spds::Core::Menu::Base` to drop its bespoke property writes and forward these args straight
   *    to its `FlexColumn`.
   * 4. `fitContent` / `fitContentHeight` / `fitContentWidth` in the `container` collection to be
   *    reconciled against `width: max-content`, so the two mechanisms stop competing for the same
   *    declaration.
   *
   * ## Blocker for that work
   *
   * `mappedProps` emits `--${dasherize(name)}`, so mapping these on the container would produce a
   * bare `--width`. That name is already in use: `Spds::Modal::Standard` sets
   * `--width: min(640px, 90vw)` as its own sizing knob, `Spds::Input::Toggle` uses it internally,
   * and consumer apps override it in several places. Custom properties inherit, so a universal
   * bare `--width` would let a modal's width leak into every sized descendant inside it. Namespace
   * the emitted properties (or scope them per component) before going universal.
   */
  sizing: {
    meta: {
      height: {
        cssProperty: 'height',
        description: 'Explicit height as a CSS length',
        type: STRING,
      },
      maxHeight: {
        cssProperty: 'max-height',
        description: 'Upper bound on height as a CSS length',
        type: STRING,
      },
      maxWidth: {
        cssProperty: 'max-width',
        description: 'Upper bound on width as a CSS length',
        type: STRING,
      },
      minHeight: {
        cssProperty: 'min-height',
        description: 'Lower bound on height as a CSS length',
        type: STRING,
      },
      minWidth: {
        cssProperty: 'min-width',
        description: 'Lower bound on width as a CSS length',
        type: STRING,
      },
      width: {
        cssProperty: 'width',
        description: 'Explicit width as a CSS length',
        type: STRING,
      },
    },
  },

  /**
   * Common form/input field args (placeholder, value, helper text, required, name).
   */
  inputForm: {
    meta: {
      charLimit: {
        description: 'Maximum character count; enables character counter',
        initialValue: 0,
        type: NUMBER,
      },
      helperText: {
        description: 'Helper or error text below the input',
        type: STRING,
      },
      hideCharacterCounter: {
        defaultValue: false,
        description: 'Hides the character counter that a character limit would otherwise display',
        type: BOOLEAN,
      },
      label: {
        description: 'Label text for the input',
        type: STRING,
      },
      name: {
        description: 'Form control name',
        type: STRING,
      },
      placeholder: {
        description: 'Placeholder text for the input',
        type: STRING,
      },
      required: {
        defaultValue: false,
        description: 'Marks the input as required',
        type: BOOLEAN,
      },
      value: {
        description: 'Current input value',
        type: STRING,
      },
    },
  },

  /**
   * Common input group/fieldset args (group name, description, layout).
   */
  inputGroup: {
    meta: {
      description: {
        description: 'Group description or legend text',
        type: STRING,
      },
      groupName: {
        description: 'Form group name; shared by radios/checkboxes in the group',
        type: STRING,
      },
      hideDescription: {
        defaultValue: false,
        description: 'Hide the group legend/description',
        type: BOOLEAN,
      },
      layout: {
        description: 'Layout direction (e.g. column, row)',
        options: SUPPORTED_LAYOUTS,
        type: STRING,
      },
    },
  },

  /**
   * Common dropdown/search menu args (no-results text, retain selection, item count, multiselect).
   */
  inputMenu: {
    meta: {
      displayedItemCount: {
        description: 'Number of items displayed in the menu',
        type: NUMBER,
      },
      multiselect: {
        defaultValue: false,
        description: 'Allow multiple selection',
        type: BOOLEAN,
      },
      noItemsDisplayed: {
        description: 'Whether the menu is currently displaying zero items',
        type: BOOLEAN,
      },
      noResultsText: {
        defaultValue: 'No results found',
        description: 'Text shown when there are no results',
        type: STRING,
      },
      retainSelection: {
        defaultValue: true,
        description: 'Keep selection visible when menu is closed',
        type: BOOLEAN,
      },
      searchable: {
        description:
          'Controls the in-menu search field. Defaults to showing automatically once the item count reaches the search threshold; pass false to disable',
        type: BOOLEAN,
      },
      searchPlaceholder: {
        description: 'Placeholder text for the in-menu search field',
        initialValue: 'Search',
        type: STRING,
      },
      searchThreshold: {
        defaultValue: 15,
        description: 'Minimum number of items before the in-menu search field is shown',
        type: NUMBER,
      },
    },
  },

  /**
   * Select-all option args for multiselect menus.
   */
  inputSelectAll: {
    meta: {
      selectAllLabel: {
        description: 'Label for the select-all option',
        type: STRING,
      },
      selectAllMin: {
        description: 'Minimum number of options to show the select-all option',
        type: NUMBER,
      },
    },
  },

  /**
   * Common single-field input args (type, checked, indeterminate, input classes, max length).
   */
  inputField: {
    meta: {
      checked: {
        defaultValue: false,
        description: 'Checked state for checkbox/radio',
        type: BOOLEAN,
      },
      component: {
        description: 'Component name for validation messages',
        type: STRING,
      },
      indeterminate: {
        defaultValue: false,
        description: 'Indeterminate state for checkbox',
        type: BOOLEAN,
      },
      inputClasses: {
        description: 'Additional CSS classes for the input element',
        type: STRING,
      },
      maxLength: {
        description: 'Maximum character length',
        type: NUMBER,
      },
      type: {
        description: 'Input type',
        options: SUPPORTED_TYPES,
        type: STRING,
      },
    },
  },

  /**
   * Common trigger/control args for dropdown and search inputs.
   */
  inputTrigger: {
    meta: {
      menuId: {
        description: 'ID of the associated menu',
        type: STRING,
      },
      menuOpenThreshold: {
        description: 'Minimum characters before opening the menu',
        type: NUMBER,
      },
      triggerId: {
        description: 'ID for the trigger element',
        type: STRING,
      },
    },
  },

  /**
   * Common modal args. Use pick(MODAL_ARGS, [...]) with overrides for initialValue per component.
   * - CTA modals (acknowledgement, transactional, destructive, announcement): closeOnClickOutside, title, primaryBtn*, secondaryBtn*
   * - Headers: close, title (optional: currentStep, stepCount for full-screen header)
   * - Full-screen footer: backBtnAction, currentStep, primaryBtn*, secondaryBtn*
   * - Full-screen root: currentStep, stepCount
   * - Flyout: closeOnClickOutside, modal, size
   */
  modal: {
    meta: {
      closeOnClickOutside: {
        defaultValue: false,
        description: 'Whether the modal closes when clicking outside',
        type: BOOLEAN,
      },
      primaryBtnAction: {
        description: 'Action for the primary footer button',
        type: ACTION,
      },
      primaryBtnDisabled: {
        defaultValue: false,
        description: 'Disables the primary footer button',
        type: BOOLEAN,
      },
      primaryBtnText: {
        description: 'Label for the primary footer button',
        initialValue: 'Confirm',
        type: STRING,
      },
      secondaryBtnAction: {
        description: 'Action for the secondary footer button',
        type: ACTION,
      },
      secondaryBtnDisabled: {
        defaultValue: false,
        description: 'Disables the secondary footer button',
        type: BOOLEAN,
      },
      secondaryBtnText: {
        description: 'Label for the secondary footer button',
        initialValue: 'Cancel',
        type: STRING,
      },
      title: {
        description: 'Modal or header title',
        type: STRING,
      },
    },
  },

  /**
   * Common tooltip args for components that support inline tooltip configuration
   */
  tooltip: {
    meta: {
      tooltipContent: {
        description: 'Content displayed inside the tooltip body',
        type: STRING,
      },
      tooltipOffset: {
        description: 'Gap between the tooltip and the anchor element, as a CSS length',
        initialValue: '8px',
        type: STRING,
      },
      tooltipPlacement: {
        defaultValue: TOP_MIDDLE,
        description: 'Position of the tooltip relative to the anchor element',
        options: PLACEMENT_OPTIONS,
        type: STRING,
      },
      tooltipTitle: {
        description: 'Optional title displayed above the tooltip body text',
        type: STRING,
      },
    },
  },

  // Computed groups for convenience (lazily computed)
  get allLayout() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.flexOnly.meta,
        ...this.gridConvenience.meta,
        ...this.gridOnly.meta,
      },
    };
  },

  get flex() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.flexOnly.meta,
      },
    };
  },

  get grid() {
    return {
      meta: {
        ...this.flexAndGrid.meta,
        ...this.gridConvenience.meta,
        ...this.gridOnly.meta,
      },
    };
  },
};

export const ACCESSIBILITY_ARGS = SHARED_ARGS.accessibility;
export const ACTION_ARGS = SHARED_ARGS.actions;
export const ALL_LAYOUT_ARGS = SHARED_ARGS.allLayout;
export const ANCHORED_ARGS = SHARED_ARGS.anchored;

/**
 * Pick spec for `ANCHORED_ARGS.placement` as menus support it. Every menu narrows the placement
 * vocabulary the same way and for the same reasons — see `VERTICAL_PLACEMENT_OPTIONS` — so the
 * narrowing lives here rather than being restated by each component that forwards the arg.
 *
 * @example
 * pick(ANCHORED_ARGS, ['anchorElement', 'anchorOffset', MENU_PLACEMENT_ARG]);
 */
export const MENU_PLACEMENT_ARG = {
  description:
    'Preferred position of the menu relative to its anchor. Only a starting point: the menu flips to the other side of the anchor when it would overflow the viewport vertically, and shifts its alignment when it would overflow horizontally.',
  name: 'placement',
  options: VERTICAL_PLACEMENT_OPTIONS,
};

export const BUTTON_ARGS = SHARED_ARGS.button;
export const COMPONENT_STATE_ARGS = SHARED_ARGS.componentState;
export const CONTAINER_ARGS = SHARED_ARGS.container;
export const CONTENT_ARGS = SHARED_ARGS.content;
export const FLEX_ARGS = SHARED_ARGS.flex;
export const GRID_ARGS = SHARED_ARGS.grid;
export const INPUT_FIELD_ARGS = SHARED_ARGS.inputField;
export const INPUT_FORM_ARGS = SHARED_ARGS.inputForm;
export const INPUT_GROUP_ARGS = SHARED_ARGS.inputGroup;
export const INPUT_MENU_ARGS = SHARED_ARGS.inputMenu;
export const INPUT_SELECT_ALL_ARGS = SHARED_ARGS.inputSelectAll;
export const INPUT_TRIGGER_ARGS = SHARED_ARGS.inputTrigger;
export const LINK_ARGS = SHARED_ARGS.link;
export const MENU_ARGS = SHARED_ARGS.menu;
export const MODAL_ARGS = SHARED_ARGS.modal;
export const POSITIONING_ARGS = SHARED_ARGS.positioning;
export const SIZING_ARGS = SHARED_ARGS.sizing;
export const TOOLTIP_ARGS = SHARED_ARGS.tooltip;

export default {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  ALL_LAYOUT_ARGS,
  BUTTON_ARGS,
  COMPONENT_STATE_ARGS,
  CONTAINER_ARGS,
  CONTENT_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  INPUT_GROUP_ARGS,
  INPUT_MENU_ARGS,
  INPUT_SELECT_ALL_ARGS,
  INPUT_TRIGGER_ARGS,
  LINK_ARGS,
  MENU_ARGS,
  MODAL_ARGS,
  POSITIONING_ARGS,
  SIZING_ARGS,
  TOOLTIP_ARGS,
};
